import { canOpenReportCalculator } from "@/calculators/reportWorkspace";
import { usePhoneExperience, phoneNavigation } from "@/lib/phoneExperience";
import { canViewEmployeeRecords } from "@/lib/employeeRecordAccess";
import {useTodayLayout} from './TodayLayoutContext';
import OpenButton from "@/components/toto/OpenButton";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CircleAlert, ChevronLeft, CalendarDays, CalendarHeart, Repeat, ArrowRightLeft, UsersRound, WalletCards, ClipboardList, FolderOpen, Package, Mail, UserRoundPlus, Printer } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FRONT_DESK_DESTINATIONS, frontDeskRouteAllowed } from "@/lib/frontDeskAccess";
import { api } from "@/lib/api";
import TotoNavigationGlyph from "@/components/TotoNavigationGlyph";
import "./TotoAdaptiveSurface.css";
import ContinuousVesselNavigation from "./ContinuousVesselNavigation";
import { useCurrentVesselNavigation } from "./VesselNavigation";

const DESTINATIONS = [
  { id: "toto", label: "TOTO", glyph: "toto" },
  { id: "today", label: "Today", glyph: "today", to: "/home" },
  { id: "team", label: "Team", glyph: "team", to: "/people" },
  { id: "workspace", label: "Workspace", glyph: "workspace", to: "/workspaces" },
];

export const TOTO_NAVIGATION_PLACEMENTS = [
  { id: "left", label: "Left · centered" },
  { id: "left-lower", label: "Left · lower" },
  { id: "right", label: "Right · centered" },
  { id: "right-lower", label: "Right · lower" },
];

// The continuous Vessel owns a fixed right edge; older saved dock placement
// values no longer move content or split the navigation into separate surfaces.
export function normalizeTotoNavigationPlacement() { return "right"; }
export function useTotoNavigationPlacement() { return "right"; }

const SCOPE_MATCHERS = {
  team: (url = "") => /^(\/people|\/operations\/(journey-planning|calendar)|\/scheduling\/(time-off|coverage|swaps))/.test(url),
  workspace: (url = "") => /^(\/calendar|\/payroll|\/scheduling|\/operations|\/documents)/.test(url),
  profile: (url = "") => /^(\/profile|\/employee-documents)/.test(url),
  settings: (url = "") => /^\/settings/.test(url),
};

export function vesselParent(path) {
  if (path === "/workspaces/performance/trackers") return {to:"/workspaces/report-calculator", label:"Report Calculators"};
  if (path === "/workspaces/hiring") return {to:"/workspaces?workspace=hiring", label:"Workspace"};
  if (path === "/operations/calendar") return {to:"/people", label:"Team"};
  if (path === "/calendar") return {to:"/workspaces", label:"Workspace"};
  if (path === "/operations/eod" || path === "/eod") return {to:"/home", label:"Today"};
  if (path.startsWith("/payroll/calculators/") || path.startsWith("/payroll/reports/")) return {to:"/payroll/prepare", label:"Payroll"};
  if (path.startsWith("/scheduling/")) return {to:"/workspaces", label:"Workspace"};
  if (/^\/(payroll|operations|documents|scheduling|workspaces\/)/.test(path)) return {to:"/workspaces", label:"Workspace"};
  return {to:"/home", label:"Today"};
}

export const WORKSPACE_DESTINATIONS = [
  {id:"hiring",label:"Hiring",Icon:UserRoundPlus,to:"/workspaces/hiring",visible:user=>["owner","coo","recruiter","onboarding_admin","boutique_manager"].includes(user?.role)},
  {id:"report-calculator",label:"Report Calculators",Icon:ClipboardList,to:"/workspaces/report-calculator",visible:canOpenReportCalculator},
  {id:"zenoti",label:"Zenoti",Icon:ClipboardList,to:"/workspaces/zenoti"},
  {id:"performance-recognition",label:"Award Ceremonies",Icon:UsersRound,to:"/workspaces/performance-recognition"},
  {id:"team-stewardship",label:"Team Stewardship",Icon:UsersRound,to:"/workspaces/team-stewardship",visible:canViewEmployeeRecords},
  {id:"emails",label:"Guided emails",Icon:Mail,to:"/operations/emails",visible:user=>["owner","coo","cfo","leadership"].includes(user?.role)},
  {id:"calendar",label:"Calendar",Icon:CalendarHeart,to:"/calendar"},
  {id:"payroll",label:"Payroll",Icon:WalletCards,to:"/payroll/readiness"},
  {id:"scheduling",label:"Scheduling",Icon:CalendarDays,to:"/scheduling"},
  {id:"journey-planning",label:"Room Assignments",Icon:ClipboardList,to:"/operations/journey-planning",visible:user=>!!user && frontDeskRouteAllowed(user,"/operations/journey-planning")},
  {id:"printable-assets",label:"Printable Assets",Icon:Printer,to:"/workspaces/printable-assets",visible:user=>["owner","coo","cfo","district_leader","leadership","boutique_manager","experience_guide","recruiter","onboarding_admin"].includes(user?.role)},
  {id:"inventory",label:"Inventory",Icon:Package,to:"/operations/inventory",visible:user=>!!user && !["front_desk","massage_therapist"].includes(user.role)},
  {id:"budget",label:"Budget",Icon:WalletCards,to:"/operations/budget",visible:user=>["owner","coo","boutique_manager","recruiter"].includes(user?.role)},
  {id:"documents",label:"Documents",Icon:FolderOpen,to:"/documents"},
];
export const SCHEDULING_DESTINATIONS = [
  {id:"eg",label:"Experience Guides",Icon:CalendarDays,to:"/scheduling/eg"},
  {id:"mt",label:"Massage Therapists",Icon:Repeat,to:"/scheduling/mt"},
  {id:"time-off",label:"Time-Off",Icon:CalendarHeart,to:"/scheduling/time-off"},
  {id:"swaps",label:"Shift Swap",Icon:ArrowRightLeft,to:"/scheduling/swaps"},
  {id:"coverage",label:"Coverage",Icon:UsersRound,to:"/scheduling/coverage"},
  {id:"zenoti",label:"Zenoti Changes",Icon:ClipboardList,to:"/scheduling/zenoti"},
];
const VESSEL_FOOTER_HOSTS = '.today-layout__vessel,.program-audit__vessel,.email-builder__vessel,.floor-plan-space__vessel,.scheduling-vessel__surface,.journey-home__arrival,.toto-workspaces__vessel,.team-space__action,.toto-profile__vessel,.payroll-assembly__vessel,.eg-review__field,.toto-review-evidence__plane,.scheduling-space__vessel,.eg-schedule-space__vessel,.operations-space__vessel,.journey-planning-space__vessel,.documents-space__vessel,.hiring-workspace__vessel,.budget-workspace__vessel,.eod-space__vessel,.inventory-space__vessel,.payroll-accepted-flow > .payroll-team-plane';

export function TotoAdaptiveDock(props) {
 const layout=useTodayLayout();
 return layout&&!props.owner?null:<AdaptiveDock {...props}/>;
}
function AdaptiveDock({ active, onBack, backLabel, actions = [], destinations }) {
  const navigate = useNavigate();
  const phone = usePhoneExperience();
  const { user, navigationPhoto } = useAuth();
  const location = useLocation();
  const config = useCurrentVesselNavigation();
  const dockRef = useRef(null);
  const [footerHost, setFooterHost] = useState(null);
  const parent = vesselParent(location.pathname);
  const lens = active || "today";
  const routeItems=location.pathname.startsWith("/scheduling")?SCHEDULING_DESTINATIONS:active==="workspace"?WORKSPACE_DESTINATIONS.filter(item=>!item.visible || item.visible(user)):null;
  const isFrontDesk = user?.role === "front_desk";
  const items=isFrontDesk?FRONT_DESK_DESTINATIONS:config?.items?.length?config.items:destinations||routeItems||DESTINATIONS.filter(item=>item.to);
  const availablePrimaryItems = isFrontDesk ? FRONT_DESK_DESTINATIONS : user?.role === "experience_guide" ? [{id:"my-day",label:"My Day",glyph:"today",to:"/my-day"},...DESTINATIONS.filter(item=>item.to)] : DESTINATIONS.filter(item => item.to);
  const primaryItems = phoneNavigation(availablePrimaryItems, phone);
  const hasPrimaryNavigation = primaryItems.every(primary => items.some(item => item.to === primary.to));
  const secondaryItems = hasPrimaryNavigation ? [] : phoneNavigation(items, phone);
  useLayoutEffect(() => {
    const next = dockRef.current?.parentElement?.querySelector(VESSEL_FOOTER_HOSTS) || null;
    setFooterHost(current => current === next ? current : next);
  });
  const tools=[...(config?.tools||[]), ...actions];
  const select=item=>{const action=()=>item.onSelect?item.onSelect():navigate(item.to);if(item.to && config?.beforeLeave?.(action)===false)return;action();};
  const leave=action=>{if(config?.beforeLeave?.(action)!==false)action();};
  return <><span ref={dockRef} hidden />
    <ContinuousVesselNavigation user={user} navigationPhoto={navigationPhoto} host={footerHost} primaryItems={primaryItems}
      destinations={secondaryItems}
      tools={tools} label={config?.label ? `${config.label} navigation` : "Secondary navigation"}
      active={isFrontDesk && location.pathname === "/profile" ? "profile" : lens} select={select} onToto={()=>leave(()=>navigate(isFrontDesk ? "/my-day" : "/home/toto"))} onProfile={()=>leave(()=>navigate("/profile"))}
      onBack={()=>leave(isFrontDesk ? (()=>navigate("/my-schedule")) : onBack||(()=>navigate(parent.to)))} backLabel={isFrontDesk ? "My Schedule" : backLabel||parent.label} />
  </>;
}

/** A consistent vessel eyebrow for detail states that replace their parent view. */
export function TotoVesselBackLink({ children, onBack, to }) {
  const navigate = useNavigate();
  const location = useLocation();
  return <button type="button" className="toto-vessel-back-link" onClick={onBack || (() => navigate(to || vesselParent(location.pathname).to))}>
    <ChevronLeft size={13} aria-hidden="true" />
    <span>{children}</span>
  </button>;
}

export function TotoSpaceAttention({ scope, empty }) {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, items: [], error: false });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let mounted = true;
    setState({ loading: true, items: [], error: false });
    api.get("/attention/feed")
      .then(({ data }) => {
        if (!Array.isArray(data?.zones?.needs_attention)) throw new Error("Attention unavailable");
        const matcher = SCOPE_MATCHERS[scope] || (() => true);
        const items = (data?.zones?.needs_attention || []).filter((item) => matcher(item.action_url));
        if (mounted) setState({ loading: false, items, error: false });
      })
      .catch(() => { if (mounted) setState({ loading: false, items: [], error: true }); });
    return () => { mounted = false; };
  }, [scope, attempt]);

  return <section className="toto-space-attention" aria-live="polite" aria-label="Needs your attention">
    <header><span>Needs your attention</span>{state.items.length > 0 && <strong>{state.items.length}</strong>}</header>
    {state.loading && <p>Checking this space.</p>}
    {!state.loading && state.error && <p>Attention could not be checked. <button type="button" onClick={() => setAttempt(value => value + 1)}>Try again</button></p>}
    {!state.loading && !state.error && state.items.length === 0 && <p>{empty || "Nothing in this space needs your attention."}</p>}
    {!state.loading && state.items.map((item) => <button type="button" key={item.id} onClick={() => navigate(item.action_url)}>
      <CircleAlert aria-hidden="true" />
      <span><b>{item.title}</b>{item.reason && <small>{item.reason}</small>}</span>
      <OpenButton as="span" aria-hidden="true" />
    </button>)}
  </section>;
}

